/**
 * 회원 현황 · 강사 현황 · 지난 리포트 탭의 목 데이터.
 *
 * 6a 검증 결과에 따라 회원 분석의 축을 '수강권 순번'으로 잡았다.
 * (순번 1 → 68.3% 이탈, 2 → 48.2%, 3 → 34.4%, 4 → 25.1%, 5+ → 17.8%)
 *
 * 모든 금액은 추정치이며 산출식을 함께 보관한다. 근거 없는 금액은 신뢰를 깎는다.
 * ⚠️ 임시 데이터입니다. 서버 API 연동 시 제거합니다.
 */

import {
    MembershipRecord,
    PassOrderResult,
    ChurnRateLookup,
    CenterRegistrationType,
    IssuanceType,
    resolvePassOrder,
    lookupChurnRate,
    resolveStage,
    CHURN_RATE_BY_ORDER,
    FIRST_PASS_CHURN_RATE,
    STABLE_RULE,
    AT_RISK_RULE,
} from './passOrder';
import { computeMemberValue, MemberValue } from './memberValue';
import { expectsReRegistration } from './adapters';
import { BriefingCompositionDto } from '../../types/opsBriefing';

export const won = (n: number) => n.toLocaleString('ko-KR');
/**
 * 120만 원 형태의 짧은 표기.
 *
 * ⚠️ 반올림이 아니라 **절사**입니다.
 * 반올림이면 15,000원이 "2만"으로 보입니다. 실제보다 큰 금액이 찍히는 쪽이
 * 이 화면에서는 더 나쁩니다 — 원장님은 이 숫자를 보고 "얼마가 걸려 있나"를
 * 판단하고, 부풀려진 금액은 있지도 않은 매출을 지키려 움직이게 만듭니다.
 * 모자라게 보이는 것은 다시 세면 되지만, 부풀려진 것은 그대로 믿습니다.
 */
export const shortWon = (n: number) => {
    // 1.99억을 "2억"으로 올리지 않는다. 소수 첫째 자리까지 절사한다.
    if (n >= 100000000) return `${(Math.floor(n / 10000000) / 10).toFixed(1).replace('.0', '')}억`;
    if (n >= 10000) return `${Math.floor(n / 10000)}만`;
    return `${n}`;
};

// ────────────────────────────────────────────────────────────
// 회원 현황 — 칸반 파이프라인
// ────────────────────────────────────────────────────────────

export type MemberStage = 'FIRST_PASS' | 'STABLE' | 'WATCH' | 'GOLDEN' | 'AT_RISK';

export const STAGE_META: Record<
    MemberStage,
    { label: string; sub: string; dot: string; bg: string; border: string; accent: string }
> = {
    FIRST_PASS: {
        label: '첫 수강권',
        // 숫자는 상수에서 — 실측으로 주기적으로 갱신된다. 문자열에 직접 적지 말 것
        sub: `라포 전체 이탈률 ${FIRST_PASS_CHURN_RATE}% · 집중 관리 구간`,
        dot: '#00A793',
        bg: '#F4FCF9',
        border: '#C6E5DA',
        accent: '#00795F',
    },
    STABLE: {
        label: '안정 궤도',
        // 순번만으로 부르지 않는다 — 네 축을 전부 통과해야 한다 (passOrder.meetsStable)
        sub: `2번째 이상 수강권 · 최근 ${STABLE_RULE.maxGapDays}일 안에 꾸준히 온 회원`,
        dot: '#3D73DD',
        bg: '#F7FAFF',
        border: '#C3D7F5',
        accent: '#2B529D',
    },
    /*
     * 위험과 안정 사이. 색도 그 사이에 둔다 —
     * 이탈 위험(회색)만큼 무겁지 않고 안정(파랑)만큼 안심시키지도 않는 톤이다.
     * 붉은색은 쓰지 않는다. 골든타임·위험과 같은 급으로 읽히면
     * 원장님이 매일 이 칸부터 열게 되고, 그건 이 단계의 뜻이 아니다.
     */
    WATCH: {
        label: '주의',
        sub: `안정 기준에 못 미치는 회원 · 아직 위험은 아닙니다`,
        dot: '#E8912A',
        bg: '#FFFBF4',
        border: '#F3DDBB',
        accent: '#B96D12',
    },
    GOLDEN: {
        label: '재등록 골든타임',
        sub: '잔여 3회 이하 또는 만료 D-14',
        dot: '#FF3E4A',
        bg: '#FFF7F7',
        border: '#F8C9CC',
        accent: '#C22D38',
    },
    AT_RISK: {
        label: '이탈 위험',
        // 노쇼는 판정에 쓰지 않는다(예측력 약함). 실제 기준은 페이스 급감과 공백이다
        sub: `소진 페이스 급감 · ${STABLE_RULE.maxGapDays}일 넘게 수업 공백`,
        dot: '#4A5568',
        bg: '#F7F8FA',
        border: '#DDE3EC',
        accent: '#2D3748',
    },
};

/**
 * 단계별 운영 가이드.
 *
 * 회원을 4단계로 나눠 놓기만 하면 "그래서 뭘 하라는 거지"가 남는다.
 * 단계마다 ① 어떤 회원인지 ② 왜 지금 봐야 하는지 ③ 뭘 하면 되는지를 같이 준다.
 * ②는 검증된 수치로만 쓴다 — 근거 없는 조언은 한 번 틀리면 다시는 안 읽힌다.
 */
export type StageGuide = {
    /** 한 줄 임팩트 — 이 단계를 한마디로 */
    headline: string;
    /** 이 단계에 언제 들어오나 */
    who: string;
    /**
     * 근거를 문단 대신 막대로 보여준다.
     * "68.3%가 두 번째로 이어지지 않습니다"는 읽어야 알지만,
     * 막대 세 개를 나란히 놓으면 안 읽어도 보인다.
     */
    stat: {
        title: string;
        unit: string;
        bars: { label: string; value: number; tone: 'BAD' | 'MID' | 'GOOD' }[];
        caption: string;
    };
    /** 이 단계에서 볼 핵심 지표 */
    watch: string;
    /** 순서대로 하면 되는 운영 동작 */
    steps: { verb: string; title: string; detail: string }[];
    /**
     * 하지 말아야 할 것 — **한 문장으로** 쓴다 (2026-08-31 QA13).
     *
     * 이 자리는 배너 한 줄이다. 두 문장이 되면 줄이 접히고, 접히면 안 읽힌다.
     * 이유가 길어지면 문장을 늘리지 말고 「이 단계에서 할 일」의 근거 툴팁으로 보낸다.
     */
    caution?: string;
};

export const STAGE_GUIDE: Record<MemberStage, StageGuide> = {
    FIRST_PASS: {
        /*
         * 실측(2024-08 백업, n=2,194)에서 1번째와 2번째 이탈률이 사실상
         * 같았다(47.1 vs 46.6). "이 구간만 넘기면 절반으로 떨어진다"던
         * 옛 문구는 데이터가 지지하지 않아 고쳤다 — 떨어지는 건 3번째부터다.
         */
        headline: `10명 중 ${Math.round(FIRST_PASS_CHURN_RATE / 10)}명이 여기서 떠납니다`,
        who: '첫 수강권을 쓰고 있는 회원입니다. 아직 센터가 습관이 되지 않았습니다.',
        stat: {
            title: '수강권 순번별 이탈률',
            unit: '%',
            bars: [
                { label: '1번째 (이 단계)', value: CHURN_RATE_BY_ORDER[1], tone: 'BAD' },
                { label: '2번째', value: CHURN_RATE_BY_ORDER[2], tone: 'BAD' },
                { label: '3번째', value: CHURN_RATE_BY_ORDER[3], tone: 'GOOD' },
            ],
            caption: '첫 두 수강권이 고비입니다. 3번째부터 이탈률이 떨어집니다',
        },
        watch: '주당 수업 횟수',
        steps: [
            {
                verb: '매주 확인',
                title: '수업 간격이 벌어지는지 보세요',
                detail: '주 1회 밑으로 떨어지면 이미 마음이 떠나는 중입니다.',
            },
            {
                verb: '미리 준비',
                title: '잔여 3회 전에 재등록 이야기를 꺼내세요',
                detail: '끝난 뒤에 연락하면 명분이 없습니다.',
            },
            {
                verb: '같이 보기',
                title: '담당 강사의 전환율을 확인하세요',
                detail: '같은 회원인데 강사에 따라 21%~58%까지 벌어집니다.',
            },
        ],
        caution: '긴 상품보다 다음 수업 일정을 먼저 잡으세요.',
    },
    STABLE: {
        headline: '여기는 손대지 않는 게 맞습니다',
        who:
            `두 번째 이상 수강권을 쓰면서 최근 ${STABLE_RULE.maxGapDays}일 안에 ` +
            '꾸준히 온 회원입니다. ' +
            '(공백이 길거나, 오는 횟수가 적거나, 페이스가 꺾였거나, ' +
            '당일 취소·변경이 잦으면 「주의」로 옮깁니다. ' +
            '다만 주당 수업이 이전보다 늘어난 회원은 여기 남습니다.)',
        stat: {
            title: '수강권 순번별 이탈률',
            unit: '%',
            bars: [
                { label: '1번째', value: CHURN_RATE_BY_ORDER[1], tone: 'BAD' },
                // 실측상 2번째도 1번째만큼 이탈한다(46.6 vs 47.1). MID 로 두면 거짓말이 된다
                { label: '2번째 (이 단계)', value: CHURN_RATE_BY_ORDER[2], tone: 'BAD' },
                { label: '3번째 (이 단계)', value: CHURN_RATE_BY_ORDER[3], tone: 'GOOD' },
            ],
            caption:
                '2번째까지는 아직 고비입니다. 다만 페이스가 정상이라면 자꾸 건드리는 것이 오히려 부담이 됩니다',
        },
        watch: '소진 페이스 변화율',
        steps: [
            {
                verb: '골라내기',
                title: '페이스가 30% 이상 꺾인 회원만 보세요',
                detail: '나머지는 그냥 두는 게 맞습니다.',
            },
            {
                verb: '기회 잡기',
                title: '소개나 후기를 부탁하기 가장 좋은 시점입니다',
                detail: '만족도가 높을 때라 응해줄 확률이 큽니다.',
            },
        ],
    },
    GOLDEN: {
        headline: '지금 말 안 하면 기회가 사라집니다',
        who: '잔여 3회 이하이거나 만료가 14일 안으로 들어온 회원입니다.',
        stat: {
            title: '상담했을 때 vs 안 했을 때 · 60일 뒤 남아 있는 비율',
            unit: '%',
            bars: [
                { label: '상담함', value: 78.6, tone: 'GOOD' },
                { label: '상담 안 함', value: 41.2, tone: 'BAD' },
            ],
            caption: '같은 시점 회원인데 상담 한 번으로 37%p가 갈립니다',
        },
        watch: '남은 수업 회차',
        steps: [
            {
                verb: '이번 주',
                title: '담당 강사에게 상담을 지시하세요',
                detail: '수업 끝나고 5분이면 됩니다. 카톡보다 대면이 훨씬 잘 됩니다.',
            },
            {
                verb: '3일 뒤',
                title: '진행이 없으면 직접 연락하세요',
                detail: '잔여 2회 밑으로 내려가면 강사를 거칠 시간이 없습니다.',
            },
            {
                verb: '제안할 때',
                title: '이전보다 회차를 늘려 권하세요',
                detail: '10회 → 20회 → 30회로 늘려온 회원은 이탈률이 절반입니다.',
            },
        ],
        caution: '만료일보다 잔여 회차를 먼저 보세요 (종료 사유의 98%가 회차 소진).',
    },
    /*
     * 🔴 문구 톤 — 안정 궤도의 "여기는 손대지 않는 게 맞습니다"와 짝입니다.
     *
     * 이 칸의 회원은 **아직 잘못된 게 없습니다.** 그래서 문구가
     * "연락하세요"로 시작하면 안 됩니다 — 그러면 원장님이 멀쩡한 회원에게
     * 이탈 방어 연락을 걸고, 그 경험이 한 번 쌓이면 명단 전체를 안 믿습니다.
     * 여기서 할 일은 **보는 것**이고, 움직이는 건 위험·골든타임 칸입니다.
     */
    WATCH: {
        headline: '아직 괜찮지만, 안심할 단계는 아닙니다',
        who:
            '이탈 위험이라고 부를 근거는 없지만 안정 궤도 기준에는 못 미치는 회원입니다. ' +
            `공백이 ${STABLE_RULE.maxGapDays}일을 넘었거나, ` +
            /*
             * 🔴 여기에 「주 N회」를 적지 마세요. 선이 센터마다 다릅니다
             *    (passOrder.meetsFrequency — 2026-08-27 대표 결정).
             */
            '오는 횟수가 그 센터 보통보다 처졌거나, ' +
            `페이스가 ${Math.abs(STABLE_RULE.paceDropPct)}% 넘게 꺾였거나, ` +
            `당일 취소·변경이 ${STABLE_RULE.sameDayAdjustments}회 이상입니다. ` +
            '(어느 항목에 걸렸는지는 회원마다 표시됩니다)',
        stat: {
            title: '수강권 순번별 이탈률',
            unit: '%',
            bars: [
                { label: '1번째', value: CHURN_RATE_BY_ORDER[1], tone: 'BAD' },
                { label: '2번째 (이 단계)', value: CHURN_RATE_BY_ORDER[2], tone: 'BAD' },
                { label: '3번째 (이 단계)', value: CHURN_RATE_BY_ORDER[3], tone: 'MID' },
            ],
            caption:
                '이 칸의 회원은 대부분 2~3번째 수강권입니다. 아직 고비 구간이라 눈에 두는 것이 맞습니다',
        },
        watch: '무엇에 걸렸는지',
        steps: [
            {
                verb: '먼저 보기',
                title: '어느 항목에 걸렸는지부터 확인하세요',
                detail: '공백과 당일 취소는 원인도 대응도 완전히 다릅니다.',
            },
            {
                verb: '강사에게',
                title: '다음 수업 때 근황을 물어보게 하세요',
                detail: '연락이 아니라 대화입니다. 이 단계에서 전화를 걸면 부담이 됩니다.',
            },
            {
                verb: '지켜보기',
                title: '2주 뒤에도 그대로면 그때 움직이세요',
                detail: `그때는 대개 이탈 위험이나 골든타임으로 내려가 있습니다.`,
            },
        ],
        caution: '재등록 이야기를 꺼낼 자리가 아닙니다.',
    },
    AT_RISK: {
        headline: '이미 떠난 회원들보다 적게 나옵니다',
        /*
         * 🔴 「마지막 수업이 14일을 넘긴 회원」은 이제 여기가 아니라 '주의'입니다.
         *    그 갈래를 WATCH 로 옮겼으므로 문구도 함께 좁힙니다 —
         *    안 고치면 화면이 서버와 다른 기준을 말합니다.
         */
        who:
            '수업 속도가 크게 떨어진 채 오래 안 나오고 있는 회원입니다. ' +
            `(소진 페이스 ${Math.abs(AT_RISK_RULE.paceDropPct)}% 이상 감소와 ` +
            `${AT_RISK_RULE.lastLessonDays}일 이상 공백이 **함께** 맞을 때입니다. ` +
            '둘 중 하나만 맞으면 「주의」에 둡니다.)',
        stat: {
            title: '최근 60일 수업 횟수 비교',
            unit: '회',
            bars: [
                { label: '이 단계 회원', value: 4, tone: 'BAD' },
                { label: '이탈한 회원 평균', value: 7, tone: 'MID' },
                { label: '재등록한 회원 평균', value: 11, tone: 'GOOD' },
            ],
            caption: '떠난 사람들보다도 적게 옵니다. 이미 이탈이 시작된 상태로 봐야 합니다',
        },
        watch: '마지막 수업일',
        steps: [
            {
                verb: '먼저 확인',
                title: '왜 안 오는지부터 물어보세요',
                detail: '부상인지, 시간이 안 맞는지에 따라 대응이 완전히 달라집니다.',
            },
            {
                verb: '일정 조정',
                title: '시간대가 문제면 요일부터 바꿔주세요',
                detail: '노쇼가 잦은 회원은 성향보다 시간대 문제인 경우가 많습니다.',
            },
            {
                verb: '2주 넘으면',
                title: '원장님이 직접 연락하세요',
                detail: '강사를 통한 연락이 이미 실패한 상태입니다.',
            },
        ],
        caution: '결제보다 다시 오게 만드는 것이 먼저입니다.',
    },
};

export type MemberCard = {
    memberId: string;
    name: string;
    coachName: string;
    stage: MemberStage;
    /**
     * 이 회원의 수강권 원본 기록.
     * 순번은 여기서 resolvePassOrder() 로 계산한다 — 숫자를 손으로 적어두면
     * 화면마다 기준이 갈리고, 재등록으로 들어온 회원을 첫 수강권으로 오판한다.
     */
    memberships: MembershipRecord[];
    /** 계산된 순번 (직접 적지 말 것) */
    passOrder: PassOrderResult;
    /** 순번에 대응하는 이탈률 안내 */
    churnHint: ChurnRateLookup;
    /** 실제 결제 정보로 계산한 금액. 단가를 모르면 null */
    value: MemberValue | null;
    /** 상품명 */
    product: string;
    totalSession: number;
    /** 잔여 회차. 회차 정보가 온전하지 않으면 null */
    remain: number | null;
    /** 만료까지 남은 일수. 만료일이 없으면 null */
    expiryDday: number | null;
    /**
     * ⚠️ **60일 창의 총 수업 수입니다. 주당 횟수가 아닙니다.**
     *
     * ─────────────────────────────────────────────────────────────
     * 전에 여기가 틀려 있었습니다
     * ─────────────────────────────────────────────────────────────
     * 목 데이터가 주당 횟수(1.5, 0.7 …)로 적혀 있었고, 그건 실서버 계약
     * (types/opsBriefing.MemberCardDto)과 다른 단위였습니다. 화면은
     * `pace.weeklyPace()` 로 한 번 더 나눠 그리므로 목 모드에서 수업량이
     * 실제보다 8.57배 작게 보였습니다.
     *
     * 표시만 틀릴 때는 넘어갔지만, **단계 판정이 이 값을 쓰기 시작하면서
     * 목 화면에서 안정 궤도가 전멸하는 문제**가 됩니다 —
     * `passOrder.meetsFrequency` 가 절대 빈도를 여기서 읽습니다.
     * 목으로 QA 하면 실서버와 다른 명단을 보게 됩니다.
     *
     * 그래서 13행을 60일 총합으로 고치고 `paceDeltaPct` 를 다시 계산했습니다
     * (반올림이 들어가 비율이 1~3pp 움직인 행이 있습니다).
     *
     * 🔴 새 목 회원을 추가할 때 **주당 횟수로 적지 마세요.** 정합성 테스트가
     *    `paceDeltaPct` 는 잡아 주지만 단위 자체는 못 잡습니다.
     *
     * (`trend` 는 주차별 값이 맞습니다 — 표시 전용이라 건드리지 않았습니다)
     */
    /** 이전 60일 수업 수 */
    paceBefore: number;
    /** 최근 60일 수업 수 */
    paceNow: number;
    /** 변화율(%). 비교할 이전 기록이 없으면 null — 0 이 아니다 */
    paceDeltaPct: number | null;
    /** 주차별 추이 (8주) */
    trend: number[];
    /** 최근 60일 노쇼율(%). 수업이 없으면 null */
    noshowPct: number | null;
    /**
     * 최근 60일 당일 취소·변경 횟수.
     *
     * 안 적은 회원은 목 출력에서 **0회**(= 재봤더니 0회)로 나갑니다.
     * `null` 을 적으면 "못 쟀다"라는 뜻이 되어 화면에 '—' 로 뜹니다 —
     * 두 상태를 목에서도 구분해 둬야 표시 분기를 눈으로 확인할 수 있습니다.
     */
    sameDayAdjustments?: number | null;
    /** 마지막 수업이 며칠 전인가. 기록이 없으면 null */
    lastLessonDaysAgo: number | null;
    /**
     * 회당 단가·결제 총액 (서버 membership.pricePerSchedule / totalPrice).
     * 금액은 이 값으로 계산한다 — 손으로 적으면 근거 없는 숫자가 된다.
     * 무료권·서비스 세션이면 null.
     */
    pricePerSchedule: number | null;
    totalPrice: number | null;
};

/**
 * 회원 원천 데이터.
 *
 * 순번(passOrder)과 단계(stage)는 여기에 적지 않는다.
 * 수강권 기록에서 계산해서 붙인다 — 손으로 적으면 재등록 회원의 순번을 고쳐도
 * 단계가 '첫 수강권'으로 남는 식으로 반드시 어긋난다.
 */
type MemberSeed = Omit<MemberCard, 'stage' | 'passOrder' | 'churnHint' | 'value'>;

/** 수강권 기록 한 줄 만들기 — 목 데이터 잡음을 줄이는 도우미 */
const ms = (
    id: string,
    activationDate: string,
    type: CenterRegistrationType = 'NEW',
    issuanceType: IssuanceType = 'PURCHASE'
): MembershipRecord => ({
    membershipId: id,
    activationDate,
    centerRegistrationType: type,
    issuanceType,
});

const memberSeeds: MemberSeed[] = [
    {
        memberId: 'm1',
        pricePerSchedule: 50000, totalPrice: 1000000, name: '이하늘', coachName: '김한솔',
        memberships: [ms('m1-1', '2026-03-14')],
        product: 'PT 20회', totalSession: 20, remain: 4, expiryDday: 11,
        paceBefore: 13, paceNow: 6, paceDeltaPct: -54, trend: [3, 3, 2, 2, 1, 1, 1, 0],
        // 3회 이상이면 화면이 붉게 세운다 (memberSignals.SAME_DAY_ADJUST_ALERT)
        noshowPct: 0, sameDayAdjustments: 3, lastLessonDaysAgo: 9,
    },
    {
        memberId: 'm2',
        pricePerSchedule: 55000, totalPrice: 825000, name: '박서준', coachName: '조은오',
        memberships: [ms('m2-1', '2026-04-02')],
        product: 'PT 15회', totalSession: 15, remain: 2, expiryDday: 6,
        paceBefore: 13, paceNow: 3, paceDeltaPct: -77, trend: [3, 3, 2, 1, 1, 0, 0, 0],
        noshowPct: 2.8, sameDayAdjustments: 1, lastLessonDaysAgo: 19,
    },
    {
        memberId: 'm3',
        pricePerSchedule: 60000, totalPrice: 1200000, name: '한수경', coachName: '김한솔',
        memberships: [ms('m3-1', '2026-03-20')],
        product: 'PT 20회', totalSession: 20, remain: 3, expiryDday: 14,
        paceBefore: 12, paceNow: 8, paceDeltaPct: -33, trend: [3, 2, 3, 2, 2, 1, 2, 1],
        noshowPct: 1.2, lastLessonDaysAgo: 5,
    },
    {
        memberId: 'm4',
        pricePerSchedule: 55000, totalPrice: 550000, name: '장윤문', coachName: '성현',
        memberships: [ms('m4-1', '2026-05-02')],
        product: 'PT 10회', totalSession: 10, remain: 6, expiryDday: 21,
        paceBefore: 10, paceNow: 9, paceDeltaPct: -10, trend: [2, 2, 2, 2, 1, 2, 1, 2],
        noshowPct: 0, lastLessonDaysAgo: 4,
    },
    {
        memberId: 'm9',
        pricePerSchedule: null, totalPrice: null, name: '최지은', coachName: '성현',
        // OT 1회는 지표 제외라 순번에서 빠진다. 이걸 세면 실제 첫 수강권이 2번째가 된다.
        memberships: [ms('m9-ot', '2026-05-28', 'EXCLUDED'), ms('m9-1', '2026-06-05')],
        product: '체험 3회', totalSession: 3, remain: 1, expiryDday: 8,
        paceBefore: 9, paceNow: 2, paceDeltaPct: -78, trend: [1, 1, 1, 0, 0, 0, 0, 0],
        // null = 못 쟀다. 0 이 아니다 — 화면은 '—' 로 적고 경고를 만들지 않는다
        noshowPct: 3.4, sameDayAdjustments: null, lastLessonDaysAgo: 12,
    },
    {
        memberId: 'm12',
        pricePerSchedule: 50000, totalPrice: 500000, name: '이은지', coachName: '김이황',
        // 라포 도입 전부터 다니던 회원. 기록은 한 건이지만 재등록으로 등록돼 있어
        // 첫 수강권이 아니다 — 예전이라면 68.3% 위험군으로 잘못 분류됐을 사례.
        memberships: [ms('m12-1', '2026-04-18', 'RE_REGISTRATION')],
        product: 'PT 10회', totalSession: 10, remain: 7, expiryDday: 39,
        paceBefore: 9, paceNow: 9, paceDeltaPct: 0, trend: [2, 1, 2, 2, 1, 2, 2, 1],
        noshowPct: 0, lastLessonDaysAgo: 4,
    },
    {
        memberId: 'm5',
        pricePerSchedule: 55000, totalPrice: 1100000, name: '김나영', coachName: '조은오',
        memberships: [ms('m5-1', '2026-01-10'), ms('m5-2', '2026-04-05')],
        product: 'PT 20회', totalSession: 20, remain: 2, expiryDday: 9,
        paceBefore: 14, paceNow: 10, paceDeltaPct: -29, trend: [3, 3, 3, 2, 2, 2, 2, 1],
        noshowPct: 0, lastLessonDaysAgo: 6,
    },
    {
        memberId: 'm6',
        pricePerSchedule: 45000, totalPrice: 1350000, name: '강도경', coachName: '김한솔',
        memberships: [ms('m6-1', '2025-12-02'), ms('m6-2', '2026-03-11')],
        product: 'PT 30회', totalSession: 30, remain: 12, expiryDday: 24,
        paceBefore: 15, paceNow: 14, paceDeltaPct: -7, trend: [4, 3, 4, 3, 3, 3, 4, 3],
        noshowPct: 0, lastLessonDaysAgo: 3,
    },
    {
        memberId: 'm10',
        pricePerSchedule: 50000, totalPrice: 1000000, name: '박미연', coachName: '김지원',
        // 아직 신규/재등록 분류가 안 된 수강권. 순번을 단정할 수 없다.
        memberships: [ms('m10-1', '2026-01-22'), ms('m10-2', '2026-04-30', 'UNCLASSIFIED')],
        product: 'PT 20회', totalSession: 20, remain: 8, expiryDday: 33,
        paceBefore: 13, paceNow: 13, paceDeltaPct: 0, trend: [3, 3, 3, 3, 3, 3, 3, 3],
        noshowPct: 0, lastLessonDaysAgo: 2,
    },
    {
        memberId: 'm13',
        pricePerSchedule: 55000, totalPrice: 1100000, name: '윤도경', coachName: '조은오',
        // 두 달 전 등록. 이전 60일 수업이 0회라 '줄었다'를 말할 수 없다.
        // 서버는 이런 회원에게 0 이 아니라 null 을 보낸다.
        memberships: [ms('m13-1', '2026-06-20')],
        product: 'PT 20회', totalSession: 20, remain: 16, expiryDday: 71,
        paceBefore: 0, paceNow: 4, paceDeltaPct: null, trend: [0, 0, 0, 1, 1, 1, 0, 1],
        // 최근 수업이 4회 있으므로 노쇼율은 낼 수 있다 (0%). null 은 수업이 0회일 때만이다.
        noshowPct: 0, lastLessonDaysAgo: 4,
    },
    {
        memberId: 'm7',
        pricePerSchedule: 48000, totalPrice: 1440000, name: '최민서', coachName: '김지원',
        memberships: [ms('m7-1', '2026-02-08'), ms('m7-2', '2026-04-12'), ms('m7-3', '2026-06-14')],
        product: 'PT 30회', totalSession: 30, remain: 3, expiryDday: 18,
        paceBefore: 13, paceNow: 14, paceDeltaPct: 8, trend: [3, 3, 4, 3, 3, 4, 3, 4],
        noshowPct: 0, lastLessonDaysAgo: 1,
    },
    {
        memberId: 'm8',
        pricePerSchedule: 52000, totalPrice: 1040000, name: '오지우', coachName: '김이황',
        memberships: [
            ms('m8-1', '2025-08-01'), ms('m8-2', '2025-11-03'),
            ms('m8-3', '2026-02-06'), ms('m8-4', '2026-05-09'),
        ],
        product: 'PT 20회', totalSession: 20, remain: 12, expiryDday: 47,
        paceBefore: 13, paceNow: 12, paceDeltaPct: -8, trend: [3, 3, 3, 3, 2, 3, 3, 3],
        noshowPct: 0, lastLessonDaysAgo: 2,
    },
    {
        memberId: 'm11',
        pricePerSchedule: 50000, totalPrice: 1000000, name: '김남훈', coachName: '조은오',
        memberships: [
            ms('m11-1', '2025-07-15'), ms('m11-2', '2025-10-20'),
            ms('m11-3', '2026-01-25'), ms('m11-4', '2026-04-28'),
        ],
        product: 'PT 20회', totalSession: 20, remain: 15, expiryDday: 58,
        paceBefore: 14, paceNow: 15, paceDeltaPct: 7, trend: [3, 4, 3, 3, 4, 3, 4, 3],
        noshowPct: 0, lastLessonDaysAgo: 1,
    },

    /* ─────────────────────────────────────────────────────────
     * 「주의」 네 갈래 — 한 사람씩
     *
     * 🔴 이 넷이 없으면 목 화면의 **주의 칸이 영영 비어 있습니다.**
     *    새 칸의 색·안내 문구·배지를 아무도 눈으로 못 보고, 「이유를 반드시
     *    댄다」는 규칙이 실제 화면에서 지켜지는지도 확인할 수 없습니다.
     *    (기존 13명은 전부 확실히 건강하거나 확실히 나쁜 회원이라
     *     경계에 서는 사람이 한 명도 없었습니다)
     *
     * 넷 다 **2번째 이상 수강권**이고(첫 수강권 칸으로 새지 않게),
     * 잔여·만료가 넉넉합니다(골든타임이 먼저 잡아가지 않게).
     * ───────────────────────────────────────────────────────── */
    {
        memberId: 'm14',
        pricePerSchedule: 52000, totalPrice: 1040000, name: '정해린', coachName: '김한솔',
        memberships: [ms('m14-1', '2025-11-05'), ms('m14-2', '2026-03-02')],
        product: 'PT 20회', totalSession: 20, remain: 8, expiryDday: 50,
        // ① 공백 — 20일 비었지만 페이스는 안 꺾였다. 위험이라 부를 근거가 없다
        paceBefore: 11, paceNow: 9, paceDeltaPct: -18, trend: [2, 2, 1, 2, 1, 1, 0, 0],
        noshowPct: 0, sameDayAdjustments: 0, lastLessonDaysAgo: 20,
    },
    {
        memberId: 'm15',
        pricePerSchedule: 60000, totalPrice: 1200000, name: '배수현', coachName: '김지원',
        memberships: [ms('m15-1', '2025-09-12'), ms('m15-2', '2026-02-20')],
        product: 'PT 20회', totalSession: 20, remain: 12, expiryDday: 60,
        /*
         * ② 오는 횟수가 적다 — 60일에 3회(주 0.4회).
         * 🔴 **변화율로는 영원히 안 걸리는 회원**이다. 원래 이 속도로 꾸준하다.
         *    절대 빈도 축이 없으면 이 사람은 계속 「안정 궤도」에 앉아 있는다.
         */
        paceBefore: 4, paceNow: 3, paceDeltaPct: -25, trend: [1, 0, 1, 0, 0, 1, 0, 0],
        noshowPct: 0, sameDayAdjustments: 0, lastLessonDaysAgo: 6,
    },
    {
        memberId: 'm16',
        pricePerSchedule: 48000, totalPrice: 960000, name: '문지호', coachName: '성현',
        memberships: [ms('m16-1', '2025-10-08'), ms('m16-2', '2026-02-14')],
        product: 'PT 20회', totalSession: 20, remain: 9, expiryDday: 50,
        // ③ 페이스 하락 — 43% 꺾였다. 위험 문턱(70%)에는 못 미친다
        paceBefore: 14, paceNow: 8, paceDeltaPct: -43, trend: [3, 3, 2, 2, 2, 1, 1, 1],
        noshowPct: 0, sameDayAdjustments: 0, lastLessonDaysAgo: 5,
    },
    {
        memberId: 'm17',
        pricePerSchedule: 55000, totalPrice: 1100000, name: '서가온', coachName: '조은오',
        memberships: [ms('m17-1', '2025-08-22'), ms('m17-2', '2026-01-30')],
        product: 'PT 20회', totalSession: 20, remain: 10, expiryDday: 45,
        // ④ 당일 취소·변경이 잦다 — 잘 나오지만 약속이 자꾸 무너진다
        paceBefore: 14, paceNow: 13, paceDeltaPct: -7, trend: [3, 3, 3, 3, 3, 3, 3, 3],
        noshowPct: 0, sameDayAdjustments: 4, lastLessonDaysAgo: 3,
    },
];

/** 순번·단계·이탈률 안내를 원천 기록에서 붙인다 */
export const mockMemberCards: MemberCard[] = memberSeeds.map((seed) => {
    const passOrder = resolvePassOrder(seed.memberships);
    /*
     * ⚠️ `lessonCount` 를 반드시 함께 넘긴다 — 실서버 경로(`adapters.toMemberView`)가
     *    그렇게 부른다. 여기서 빠뜨리면 "비교 기준은 없지만 오래 안 나온 회원"이
     *    목에서는 안 걸리고 실서버에서는 이탈 위험으로 뜬다. 같은 회원을 두고
     *    목과 실화면이 다른 말을 하는 자리라, 목으로 QA 하면 못 본다.
     */
    const stage = resolveStage(
        { ...seed, lessonCount: seed.paceNow + seed.paceBefore },
        passOrder
    );
    return {
        ...seed,
        passOrder,
        stage,
        churnHint: lookupChurnRate(passOrder),
        /*
         * 🔴 실서버 경로(`adapters.toMemberView`)와 **같은 함수**로 판정한다.
         *    여기에 조건을 다시 적으면 목과 실화면의 금액이 갈리고,
         *    그러면 목으로 QA 하는 동안 그 차이가 안 보인다.
         */
        value: computeMemberValue(seed, passOrder, {
            expectsReRegistration: expectsReRegistration(stage),
        }),
    };
});

/** 첫 수강권 → 두 번째 전환 퍼널 (회원 국면 — 재등록 성공률과 다른 지표다) */
export const mockConversionFunnel = {
    month: '2026년 8월',
    firstPassEnded: 12,
    consulted: 7,
    converted: 3,
    lastMonthConvertedPct: 33.3,
};

// ────────────────────────────────────────────────────────────
// 강사 현황
// ────────────────────────────────────────────────────────────

/** 비율의 근거 — 분자 / 분모 */
export type MetricBasis = { numerator: number; denominator: number };

export type CoachRow = {
    coachId: string;
    name: string;
    members: number;
    firstPassMembers: number;
    lessons: number;
    /**
     * 담당 회원 중 실제로 활성 수강권을 갖고 수업받는 회원 수.
     * 수업량을 그대로 비교하면 담당이 적은 강사가 부당하게 낮게 잡힌다.
     * 회원당 수업 수를 내려면 이 값이 필요하다 (서버: membership.status === 'active').
     */
    activeMembers: number;
    /**
     * 🆕 **그 달에 실제로 담당한 회원 수** (백엔드 f78c887c, 2026-08-31).
     *
     * `activeMembers` 는 「오늘」 기준이라 분자(그 달 수업 수)와 기준시각이
     * 달랐다. 판정에 쓰는 분모는 `coachAnalysis.judgementMembers` 하나로만
     * 고른다 — 여기 값을 직접 읽어 나누지 말 것.
     *
     * 키가 없으면 구버전 서버(또는 목)라 `activeMembers` 로 폴백한다.
     */
    monthlyMembers?: number;
    /**
     * **그 달에 실제로 수업한 회원 수.**
     *
     * 「회원당 수업 수」의 분모다. 수강권만 있고 한 번도 안 온 잠수회원이
     * 분모에 들어가 값이 구조적으로 낮게 나오던 것을 고친다.
     * 분모는 `coachAnalysis.lessonDensityOf` 한 곳에서만 고른다 —
     * 여기 값을 직접 읽어 나누지 말 것.
     *
     * ⚠️ **표본 게이트·성숙도는 계속 `judgementMembers` 를 본다.** 밀도만 바뀐다.
     * 키가 없으면 구버전 서버(또는 목)라 `monthlyMembers ?? activeMembers` 로 떨어진다.
     */
    lessonMembers?: number | null;
    /** 센터 내 수업 순위 */
    lessonRank: number;
    /**
     * 재등록 성공률 — 분모는 **그 달에 정상 종료한 회원 수**다.
     *
     * ⚠️ `null` 은 「표본 부족」이 아니라 **분모가 0** 이라는 뜻이다
     *    (서버 `opsCoaches.allPassConversionOf` — `eligible = 분모 > 0`).
     */
    /*
     * 아래는 전부 "재봤더니 이 값"과 "아직 못 쟀다"를 구분한다.
     * null 은 후자다 — 0 으로 적으면 못 잰 지표로 강사가 평가받는다.
     */
    conversionPct: number | null;
    sameDayCancel: number | null;
    responseHours: number | null;
    pendingRequests: number | null;
    journalPct: number | null;
    /** 회원이 일지를 연 비율 */
    journalReadPct: number | null;
    noshowPct: number | null;
    /** 담당 회원 부상 기록 비율 */
    injuryPct: number | null;
    /** 앱 미연동 회원 수 */
    appUnlinked: number | null;
    /** 성실도 종합 (0~100) — 일지·응답속도·당일취소 가중 */
    diligenceScore: number | null;
    /**
     * 비율 지표의 분자·분모. 리포트가 `31.7%` 아래에 `19/60` 을 찍는다.
     * 서버가 안 보내면 undefined — 없는 근거를 지어내지 않는다.
     */
    basis?: {
        conversion: MetricBasis | null;
        closing: MetricBasis | null;
        journal: MetricBasis | null;
        noshow: MetricBasis | null;
    } | null;
    /** 담당 회원 잔여 수강권 가치 */
    portfolioValue: number;
    /** 이번 달 매출(원) */
    revenue: number | null;
    newRegistrations: number | null;
    reRegistrations: number | null;
    /** 지난달 값 — 흐름을 보려면 필요하다 */
    prev?: {
        conversionPct: number | null;
        closingRate: number | null;
        journalPct: number | null;
        lessons: number | null;
        /** 지난달 「수업 회원」 — 밀도 분모 */
        lessonMembers?: number | null;
        revenue: number | null;
    };
    /**
     * 신규기회 클로징률(%). 신규기회를 배정받지 않은 강사는 null.
     * 재등록 전환율(관계 유지력)과 다른 능력이라 따로 둔다.
     */
    closingRate: number | null;
    flags: string[];
    aiSummary: string;
};

/**
 * 센터 평균 — 강사 비교의 기준선.
 * 값이 없는 지표는 null 이고, 그 지표는 비교 자체를 하지 않는다.
 */
export type CenterAvg = {
    conversionPct: number | null;
    /** ⚠️ 「오늘」 담당 기준. 월 단위 비교에는 `lessonsPerMonthlyMember` 를 쓴다 */
    lessonsPerActiveMember: number | null;
    /**
     * 🆕 그 달 담당 회원 기준 평균 — `CoachRow.monthlyMembers` 와 **쌍**이다.
     * 고르는 것은 `coachAnalysis.lessonDensityAvgOf` 한 곳뿐이다.
     */
    lessonsPerMonthlyMember?: number | null;
    /**
     * 그 달 **수업한** 회원 기준 평균 — `CoachRow.lessonMembers` 와 **쌍**이다.
     * 고르는 것은 `coachAnalysis.lessonDensityAvgOf` 한 곳뿐이다.
     */
    lessonsPerLessonMember?: number | null;
    closingRate: number | null;
    sameDayCancel: number | null;
    responseHours: number | null;
    journalPct: number | null;
    journalReadPct: number | null;
    noshowPct: number | null;
    injuryPct: number | null;
};

export const COACH_CENTER_AVG: CenterAvg = {
    conversionPct: 31.7,
    /** 활성 회원 1명당 월 수업 수 — 수업량을 회원 수로 나눈 밀도 */
    lessonsPerActiveMember: 11.2,
    /*
     * 그 달 담당 기준 평균. **실서버가 둘 다 주므로 목도 둘 다 준다** —
     * 목이 구서버 모양이면 목으로 QA 하는 동안 월 스코프 경로를 한 번도 안 탄다
     * (`mockData.ValueImpact` 가 같은 이유로 실서버와 갈렸던 전례가 있다).
     * 구서버 폴백은 목이 아니라 테스트가 지킨다(coachAnalysis.test).
     */
    lessonsPerMonthlyMember: 10.4,
    /** 신규기회 클로징률 센터 평균 */
    closingRate: 21.7,
    sameDayCancel: null,
    responseHours: null,
    journalPct: 48,
    journalReadPct: null,
    noshowPct: 0.9,
    injuryPct: null,
};

/**
 * 경고가 아니라 칭찬에 가까운 태그.
 * 전부 주황으로 칠하면 잘하는 강사도 문제 있는 것처럼 보인다.
 */
export const POSITIVE_FLAGS = new Set(['보상·사례 공유 필요']);

export const isPositiveFlag = (flag: string) => POSITIVE_FLAGS.has(flag);

export const mockCoachRows: CoachRow[] = [
    {
        coachId: 'c1', name: '김한솔', members: 12, firstPassMembers: 5, lessons: 143, activeMembers: 11, monthlyMembers: 12, lessonRank: 3,
        revenue: 4800000, newRegistrations: 2, reRegistrations: 4,
        prev: { conversionPct: 38.0, closingRate: null, journalPct: 92, lessons: 118, revenue: 4100000 },
        conversionPct: 58.3, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 100, journalReadPct: null, noshowPct: 0.7, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 5400000,
        closingRate: 25,
        basis: { conversion: { numerator: 7, denominator: 12 }, closing: { numerator: 1, denominator: 4 }, journal: { numerator: 143, denominator: 143 }, noshow: { numerator: 1, denominator: 143 } },

        flags: ['보상·사례 공유 필요'],
        aiSummary: '전환율과 일지 작성률이 모두 센터 1위입니다. 무엇을 다르게 하는지 다른 강사와 공유할 가치가 있습니다.',
    },
    {
        coachId: 'c2', name: '조은오', members: 15, firstPassMembers: 6, lessons: 194, activeMembers: 14, monthlyMembers: 13, lessonRank: 1,
        revenue: 2600000, newRegistrations: 1, reRegistrations: 2,
        prev: { conversionPct: 19.5, closingRate: null, journalPct: 90, lessons: 41, revenue: 3000000 },
        conversionPct: 44.4, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 80.9, journalReadPct: null, noshowPct: 1.0, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 6900000,
        closingRate: null,
        basis: { conversion: { numerator: 8, denominator: 18 }, closing: null, journal: { numerator: 157, denominator: 194 }, noshow: { numerator: 2, denominator: 194 } },

        flags: [],
        aiSummary: '수업량이 센터 1위인데도 지표가 안정적입니다. 다만 담당 회원이 15명으로 많아 부하를 지켜봐야 합니다.',
    },
    {
        coachId: 'c3', name: '성현', members: 14, firstPassMembers: 7, lessons: 182, activeMembers: 13, monthlyMembers: 15, lessonRank: 2,
        revenue: 1900000, newRegistrations: 1, reRegistrations: 1,
        prev: { conversionPct: 25.0, closingRate: null, journalPct: 61, lessons: 55, revenue: 2400000 },
        conversionPct: 21.4, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 72, journalReadPct: null, noshowPct: 0.5, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 4500000,
        closingRate: 33.3,
        basis: { conversion: { numerator: 3, denominator: 14 }, closing: { numerator: 1, denominator: 3 }, journal: { numerator: 131, denominator: 182 }, noshow: { numerator: 1, denominator: 182 } },

        flags: ['당일취소 급증', '예약 응답 지연', '전환율 저조'],
        aiSummary:
            '수업량은 상위권인데 약속 관련 지표만 나쁩니다. 태도 문제로 단정하기 전에 과부하 여부를 먼저 확인하세요. 첫 수강권 회원을 7명이나 맡고 있어 전환율 21.4%의 영향이 큽니다.',
    },
    {
        coachId: 'c4', name: '김지원', members: 13, firstPassMembers: 4, lessons: 128, activeMembers: 12, monthlyMembers: 10, lessonRank: 4,
        revenue: 6100000, newRegistrations: 3, reRegistrations: 5,
        prev: { conversionPct: 44.0, closingRate: 85.0, journalPct: 84, lessons: 121, revenue: 5500000 },
        conversionPct: 50.0, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 88.3, journalReadPct: null, noshowPct: 0.8, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 5100000,
        closingRate: 100,
        basis: { conversion: { numerator: 2, denominator: 4 }, closing: { numerator: 2, denominator: 2 }, journal: { numerator: 113, denominator: 128 }, noshow: { numerator: 1, denominator: 128 } },

        flags: [],
        aiSummary: '전환율 50%로 안정적입니다. 담당 회원 관리가 고르게 이루어지고 있습니다.',
    },
    {
        coachId: 'c5', name: '김이황', members: 26, firstPassMembers: 9, lessons: 97, activeMembers: 19, monthlyMembers: 22, lessonRank: 5,
        revenue: 3400000, newRegistrations: 2, reRegistrations: 3,
        prev: { conversionPct: 30.0, closingRate: null, journalPct: 55, lessons: 76, revenue: 3300000 },
        conversionPct: 33.3, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 97.9, journalReadPct: null, noshowPct: 1.0, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 8200000,
        closingRate: 7.7,
        basis: { conversion: { numerator: 1, denominator: 3 }, closing: { numerator: 1, denominator: 13 }, journal: { numerator: 95, denominator: 97 }, noshow: { numerator: 1, denominator: 97 } },

        flags: ['담당 회원 과다'],
        aiSummary:
            '담당 회원이 26명으로 센터 최다입니다. 일지는 98%로 성실하지만 1인당 관리 밀도가 떨어질 수 있습니다. 배정 조정을 검토하세요.',
    },
    {
        coachId: 'c6', name: '한은지', members: 9, firstPassMembers: 3, lessons: 41, activeMembers: 7, monthlyMembers: 8, lessonRank: 7,
        revenue: 2200000, newRegistrations: 1, reRegistrations: 2,
        prev: { conversionPct: 22.0, closingRate: null, journalPct: 70, lessons: 48, revenue: 2000000 },
        conversionPct: 25.0, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 95, journalReadPct: null, noshowPct: 4.2, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 2800000,
        closingRate: 0,
        flags: ['노쇼 집중'],
        aiSummary:
            '성실도는 높은데 담당 회원 노쇼율이 센터 평균의 4.7배입니다. 개인 역량보다 시간대·회원 배정 문제일 가능성이 큽니다.',
    },
    {
        coachId: 'c7', name: '정하림', members: 7, firstPassMembers: 2, lessons: 33, activeMembers: 5, monthlyMembers: 6, lessonRank: 8,
        revenue: 900000, newRegistrations: 0, reRegistrations: 1,
        prev: { conversionPct: null, closingRate: null, journalPct: 40, lessons: 12, revenue: 700000 },
        conversionPct: null, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 42, journalReadPct: null, noshowPct: 1.2, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 1900000,
        closingRate: null,
        flags: ['일지 작성률 낮음', '표본 부족'],
        aiSummary:
            '담당 회원이 7명뿐이라 전환율을 판단할 표본이 부족합니다. 일지 작성률 42%는 센터 평균 이하로 개선이 필요합니다.',
    },
    {
        coachId: 'c8', name: '강혜빈', members: 11, firstPassMembers: 4, lessons: 88, activeMembers: 10, monthlyMembers: 11, lessonRank: 6,
        revenue: 3900000, newRegistrations: 2, reRegistrations: 3,
        prev: { conversionPct: 36.0, closingRate: null, journalPct: 95, lessons: 82, revenue: 3500000 },
        conversionPct: 40.0, sameDayCancel: null, responseHours: null, pendingRequests: null,
        journalPct: 97, journalReadPct: null, noshowPct: 0.9, injuryPct: null, appUnlinked: null,
        diligenceScore: null, portfolioValue: 4200000,
        closingRate: null,
        flags: [],
        aiSummary: '모든 지표가 센터 평균 이상입니다. 안정적으로 운영되고 있습니다.',
    },
];


// ────────────────────────────────────────────────────────────
// 신규기회 클로징 (서버: newOpportunity)
// ────────────────────────────────────────────────────────────

/**
 * 신규기회 = 체험·OT·인수인계처럼 수업 전 단계의 전환 후보 일정.
 * 강사가 배정받아 진행하고, 실제 수강권 결제로 이어지면 클로징 성공이다.
 *
 * 중요한 점 — 클로징률은 강사가 손으로 찍는 closingStatus 가 아니라
 * 실제 활성 수강회원 전환 여부로 계산한다(서버 getConversionDateMap).
 * 그래서 "성공이라고 눌러놨는데 결제는 안 된" 건이 성과로 잡히지 않는다.
 *
 * 재등록 전환율(기존 회원을 이어가는 능력)과는 다른 능력이다.
 *   재등록 전환율 = 관계 유지력
 *   신규 클로징률 = 첫 만남에서 결제까지 끌고 가는 힘
 * 둘을 합쳐 하나로 만들면 어느 쪽이 약한지 안 보인다. 그래서 따로 둔다.
 */
export type ClosingByType = {
    name: string;
    /** 이 유형으로 만난 회원 수 */
    leadMembers: number;
    /** 진행 건수 */
    opportunities: number;
    /** 결제까지 간 회원 수 */
    closed: number;
};

export type CoachClosing = {
    coachId: string;
    name: string;
    /** 신규기회로 만난 회원 수 (중복 제거) */
    leadMembers: number;
    /** 진행한 기회 건수 */
    opportunities: number;
    /** 노쇼된 건수 */
    noShow: number;
    /** 결제까지 간 회원 수 */
    closed: number;
    /** closed / leadMembers × 100 */
    closingRate: number;
    /** 평균 몇 번째 기회에서 결제됐나 */
    avgOrderToClose: number | null;
    /** 신규기회 일지 작성률 */
    journalRate: number;
    /** 클로징된 회원 이름 (원장이 바로 알아보게) */
    closedNames: string[];
    byType: ClosingByType[];
};

export const CENTER_CLOSING = {
    month: '2026년 8월',
    leadMembers: 23,
    opportunities: 25,
    closed: 5,
    /** 센터 평균 클로징률 — 강사 비교 기준선 */
    closingRate: 21.7,
    journalRate: 21.1,
    avgPerMember: 1.1,
    byType: [
        { name: '체험', leadMembers: 11, opportunities: 12, closed: 2 },
        { name: 'OT', leadMembers: 7, opportunities: 7, closed: 1 },
        { name: '인수인계', leadMembers: 5, opportunities: 6, closed: 2 },
    ] as ClosingByType[],
};

export const mockCoachClosing: CoachClosing[] = [
    {
        coachId: 'c4', name: '김지원',
        leadMembers: 2, opportunities: 2, noShow: 0, closed: 2, closingRate: 100,
        avgOrderToClose: 1, journalRate: 100, closedNames: ['이진희', '홍성민'],
        byType: [{ name: '인수인계', leadMembers: 2, opportunities: 2, closed: 2 }],
    },
    {
        coachId: 'c3', name: '성현',
        leadMembers: 6, opportunities: 6, noShow: 1, closed: 2, closingRate: 33.3,
        avgOrderToClose: 1, journalRate: 0, closedNames: ['우지연', '김춘화'],
        byType: [
            { name: '체험', leadMembers: 4, opportunities: 4, closed: 2 },
            { name: 'OT', leadMembers: 2, opportunities: 2, closed: 0 },
        ],
    },
    {
        coachId: 'c1', name: '김한솔',
        leadMembers: 4, opportunities: 4, noShow: 0, closed: 1, closingRate: 25,
        avgOrderToClose: 1, journalRate: 100, closedNames: ['장민석'],
        byType: [
            { name: '체험', leadMembers: 3, opportunities: 3, closed: 1 },
            { name: 'OT', leadMembers: 1, opportunities: 1, closed: 0 },
        ],
    },
    {
        coachId: 'c5', name: '김이황',
        leadMembers: 13, opportunities: 15, noShow: 2, closed: 1, closingRate: 7.7,
        avgOrderToClose: 1, journalRate: 0, closedNames: ['우지연'],
        byType: [
            { name: '체험', leadMembers: 6, opportunities: 7, closed: 1 },
            { name: 'OT', leadMembers: 4, opportunities: 4, closed: 0 },
            { name: '인수인계', leadMembers: 3, opportunities: 4, closed: 0 },
        ],
    },
    {
        coachId: 'c6', name: '한은지',
        leadMembers: 2, opportunities: 2, noShow: 0, closed: 0, closingRate: 0,
        avgOrderToClose: null, journalRate: 100, closedNames: [],
        byType: [{ name: 'OT', leadMembers: 2, opportunities: 2, closed: 0 }],
    },
];

// ────────────────────────────────────────────────────────────
// 지난 리포트 — 경영 회고
// ────────────────────────────────────────────────────────────

export type DayReport = {
    date: string;
    items: number;
    acted: number;
    dismissed: number;
    opened: boolean;
    /** 그날 지켜낸 금액. 조치 결과 추적 전이면 null */
    defended: number | null;
    /** 그날 놓친 금액 (미실행 후 이탈). 모르면 null */
    lost: number | null;
    /**
     * 그날 편성 결과 — 후보가 몇이었고 무엇이 브리핑에 못 올랐나.
     *
     * ⚠️ **null / 없음은 "모른다"** 입니다. 0 으로 채워 읽지 마세요.
     * (편성 기록을 남기기 전 구버전 문서이거나, 아직 목 데이터인 경우입니다)
     * 서버 계약은 types/opsBriefing.ts 의 DayReportDto.composition 입니다.
     */
    composition?: BriefingCompositionDto | null;
    highlights: {
        grade: 'DANGER' | 'WARNING' | 'CHANCE' | 'PRAISE';
        text: string;
        done: boolean;
        /** 판단의 핵심 수치 — 목록에서 바로 심각도를 알 수 있게 */
        facts?: string[];
        coachName?: string;
    }[];
};

const mk = (
    date: string, items: number, acted: number, dismissed: number, opened: boolean,
    defended: number | null, lost: number | null, highlights: DayReport['highlights'] = []
): DayReport => ({ date, items, acted, dismissed, opened, defended, lost, highlights });

export const mockDayReports: DayReport[] = [
    mk('2026-07-01', 5, 3, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '이진희 회원 노쇼 반복', done: true },
        { grade: 'DANGER', text: '이은지 회원 수업 중단', done: true },
        { grade: 'WARNING', text: '김이황 강사 예약 응답 지연', done: true },
        { grade: 'CHANCE', text: '우지연 회원 신규기회 클로징 시점', done: false },
        { grade: 'PRAISE', text: '조은오 강사 일지 작성 100%', done: false },
    ]),
    mk('2026-07-02', 3, 1, 1, true, 600000, 0, [
        { grade: 'DANGER', text: '강도경 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '김지원 강사 담당 회원 노쇼 집중', done: false },
        { grade: 'CHANCE', text: '한수경 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-03', 3, 1, 0, true, 300000, 0, [
        { grade: 'DANGER', text: '한수경 회원 페이스 절반으로 하락', done: true },
        { grade: 'WARNING', text: '조은오 강사 당일취소 증가', done: false },
        { grade: 'CHANCE', text: '최민서 회원 재등록 골든타임', done: false },
    ]),
    mk('2026-07-04', 3, 1, 0, true, 300000, 0, [
        { grade: 'DANGER', text: '김나영 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '강혜빈 강사 당일취소 증가', done: false },
        { grade: 'CHANCE', text: '박미연 회원 재등록 골든타임', done: false },
    ]),
    mk('2026-07-05', 0, 0, 0, true, 0, 0),
    mk('2026-07-06', 3, 1, 0, true, 300000, 0, [
        { grade: 'DANGER', text: '이하늘 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '김한솔 강사 당일취소 증가', done: false },
        { grade: 'CHANCE', text: '이은지 회원 마일스톤 도달', done: false },
    ]),
    mk('2026-07-07', 4, 2, 1, true, 600000, 400000, [
        { grade: 'DANGER', text: '최지은 회원 노쇼 반복', done: true },
        { grade: 'DANGER', text: '홍성민 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '김지원 강사 예약 응답 지연', done: false },
        { grade: 'CHANCE', text: '박서준 회원 재등록 골든타임', done: false },
    ]),
    mk('2026-07-08', 5, 3, 0, true, 900000, 800000, [
        { grade: 'DANGER', text: '이하늘 회원 페이스 절반으로 하락', done: true },
        { grade: 'DANGER', text: '한수경 회원 21일 미출석', done: true },
        { grade: 'WARNING', text: '한은지 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '최지은 회원 신규기회 클로징 시점', done: false },
        { grade: 'PRAISE', text: '성현 강사 전환율 센터 1위', done: false },
    ]),
    mk('2026-07-09', 4, 2, 0, true, 600000, 800000, [
        { grade: 'DANGER', text: '김나영 회원 수업 중단', done: true },
        { grade: 'DANGER', text: '김춘화 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '김한솔 강사 담당 회원 노쇼 집중', done: false },
        { grade: 'CHANCE', text: '박미연 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-10', 3, 2, 0, true, 900000, 400000, [
        { grade: 'DANGER', text: '이은지 회원 12일 미출석', done: true },
        { grade: 'WARNING', text: '김이황 강사 예약 응답 지연', done: true },
        { grade: 'CHANCE', text: '한수경 회원 마일스톤 도달', done: false },
    ]),
    mk('2026-07-11', 3, 2, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '박서준 회원 15일 미출석', done: true },
        { grade: 'WARNING', text: '조은오 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '오지우 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-12', 0, 0, 0, true, 0, 0),
    mk('2026-07-13', 5, 1, 1, true, 300000, 1200000, [
        { grade: 'DANGER', text: '김춘화 회원 페이스 절반으로 하락', done: true },
        { grade: 'DANGER', text: '한수경 회원 수강권 소진 임박', done: false },
        { grade: 'WARNING', text: '조은오 강사 당일취소 증가', done: false },
        { grade: 'CHANCE', text: '박서준 회원 마일스톤 도달', done: false },
        { grade: 'PRAISE', text: '조은오 강사 전환율 센터 1위', done: false },
    ]),
    mk('2026-07-14', 4, 2, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '이하늘 회원 노쇼 반복', done: true },
        { grade: 'DANGER', text: '한수경 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '예약 요청 3건 미응답', done: false },
        { grade: 'CHANCE', text: '이진희 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-15', 5, 3, 0, true, 900000, 800000, [
        { grade: 'DANGER', text: '최민서 회원 노쇼 반복', done: true },
        { grade: 'DANGER', text: '장윤문 회원 수업 중단', done: true },
        { grade: 'WARNING', text: '한은지 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '이은지 회원 신규기회 클로징 시점', done: false },
        { grade: 'PRAISE', text: '한은지 강사 전환율 센터 1위', done: false },
    ]),
    mk('2026-07-16', 5, 3, 0, true, 1350000, 0, [
        { grade: 'DANGER', text: '홍성민 회원 19일 미출석', done: true },
        { grade: 'DANGER', text: '오지우 회원 12일 미출석', done: true },
        { grade: 'WARNING', text: '김한솔 강사 당일취소 증가', done: true },
        { grade: 'CHANCE', text: '장윤문 회원 마일스톤 도달', done: false },
        { grade: 'PRAISE', text: '강혜빈 강사 담당 이탈 0명', done: false },
    ]),
    mk('2026-07-17', 3, 2, 0, true, 600000, 400000, [
        { grade: 'DANGER', text: '오지우 회원 페이스 절반으로 하락', done: true },
        { grade: 'WARNING', text: '김한솔 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '최민서 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-18', 3, 2, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '이진희 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '김한솔 강사 당일취소 증가', done: true },
        { grade: 'CHANCE', text: '김남훈 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-19', 0, 0, 0, true, 0, 0),
    mk('2026-07-20', 5, 2, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '김춘화 회원 9일 미출석', done: true },
        { grade: 'DANGER', text: '박서준 회원 15일 미출석', done: true },
        { grade: 'WARNING', text: '강혜빈 강사 예약 응답 지연', done: false },
        { grade: 'CHANCE', text: '우지연 회원 마일스톤 도달', done: false },
        { grade: 'PRAISE', text: '강혜빈 강사 담당 이탈 0명', done: false },
    ]),
    mk('2026-07-21', 3, 2, 0, true, 900000, 400000, [
        { grade: 'DANGER', text: '이진희 회원 9일 미출석', done: true },
        { grade: 'WARNING', text: '예약 요청 3건 미응답', done: true },
        { grade: 'CHANCE', text: '오지우 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-07-22', 3, 2, 0, true, 1200000, 0, [
        { grade: 'DANGER', text: '한수경 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '정하림 강사 당일취소 증가', done: true },
        { grade: 'CHANCE', text: '홍성민 회원 재등록 골든타임', done: false },
    ]),
    mk('2026-07-23', 5, 3, 0, true, 1800000, 0, [
        { grade: 'DANGER', text: '최지은 회원 21일 미출석', done: true },
        { grade: 'DANGER', text: '김춘화 회원 19일 미출석', done: true },
        { grade: 'WARNING', text: '김이황 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '최민서 회원 신규기회 클로징 시점', done: false },
        { grade: 'PRAISE', text: '김지원 강사 전환율 센터 1위', done: false },
    ]),
    mk('2026-07-24', 3, 2, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '이하늘 회원 수강권 소진 임박', done: true },
        { grade: 'WARNING', text: '예약 요청 2건 미응답', done: true },
        { grade: 'CHANCE', text: '한수경 회원 마일스톤 도달', done: false },
    ]),
    mk('2026-07-25', 3, 2, 0, true, 600000, 0, [
        { grade: 'DANGER', text: '강도경 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '정하림 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '홍성민 회원 마일스톤 도달', done: false },
    ]),
    mk('2026-07-26', 0, 0, 0, true, 0, 0),
    mk('2026-07-27', 5, 2, 1, true, 900000, 0, [
        { grade: 'DANGER', text: '박서준 회원 12일 미출석', done: true },
        { grade: 'DANGER', text: '최민서 회원 페이스 절반으로 하락', done: true },
        { grade: 'WARNING', text: '정하림 강사 당일취소 증가', done: false },
        { grade: 'CHANCE', text: '김나영 회원 재등록 골든타임', done: false },
        { grade: 'PRAISE', text: '김지원 강사 담당 이탈 0명', done: false },
    ]),
    mk('2026-07-28', 3, 2, 0, true, 900000, 400000, [
        { grade: 'DANGER', text: '김나영 회원 15일 미출석', done: true },
        { grade: 'WARNING', text: '한은지 강사 예약 응답 지연', done: true },
        { grade: 'CHANCE', text: '장윤문 회원 재등록 골든타임', done: false },
    ]),
    mk('2026-07-29', 5, 4, 0, true, 1800000, 400000, [
        { grade: 'DANGER', text: '박미연 회원 수강권 소진 임박', done: true },
        { grade: 'DANGER', text: '최지은 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '한은지 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '김나영 회원 마일스톤 도달', done: true },
        { grade: 'PRAISE', text: '정하림 강사 일지 작성 100%', done: false },
    ]),
    mk('2026-07-30', 5, 4, 0, true, 1800000, 400000, [
        { grade: 'DANGER', text: '장윤문 회원 12일 미출석', done: true },
        { grade: 'DANGER', text: '장윤문 회원 19일 미출석', done: true },
        { grade: 'WARNING', text: '예약 요청 3건 미응답', done: true },
        { grade: 'CHANCE', text: '김남훈 회원 재등록 골든타임', done: true },
        { grade: 'PRAISE', text: '김한솔 강사 일지 작성 100%', done: false },
    ]),
    mk('2026-07-31', 5, 5, 0, true, 1500000, 0, [
        { grade: 'DANGER', text: '이진희 회원 페이스 절반으로 하락', done: true },
        { grade: 'DANGER', text: '이은지 회원 수업 중단', done: true },
        { grade: 'WARNING', text: '강혜빈 강사 예약 응답 지연', done: true },
        { grade: 'CHANCE', text: '박미연 회원 재등록 골든타임', done: true },
        { grade: 'PRAISE', text: '한은지 강사 담당 이탈 0명', done: true },
    ]),
    mk('2026-08-01', 5, 5, 0, true, 2100000, 0, [
        { grade: 'DANGER', text: '한수경 회원 소진 임박', done: true },
        { grade: 'WARNING', text: '성현 강사 당일취소 증가', done: true },
        { grade: 'CHANCE', text: '박미연 회원 재등록 시점', done: true },
        { grade: 'PRAISE', text: '김한솔 강사 일지 100%', done: true },
        { grade: 'DANGER', text: '최지은 회원 12일 미출석', done: true },
    ]),
    mk('2026-08-02', 0, 0, 0, true, 0, 0),
    mk('2026-08-03', 4, 2, 0, true, 600000, 0, [
        { grade: 'DANGER', text: '이은지 회원 12일 미출석', done: true },
        { grade: 'DANGER', text: '오지우 회원 노쇼 반복', done: true },
        { grade: 'WARNING', text: '김한솔 강사 예약 응답 지연', done: false },
        { grade: 'CHANCE', text: '오지우 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-08-04', 4, 3, 0, true, 900000, 0, [
        { grade: 'DANGER', text: '한수경 회원 수업 중단', done: true },
        { grade: 'DANGER', text: '장윤문 회원 수업 중단', done: true },
        { grade: 'WARNING', text: '김지원 강사 담당 회원 노쇼 집중', done: true },
        { grade: 'CHANCE', text: '박서준 회원 신규기회 클로징 시점', done: false },
    ]),
    mk('2026-08-05', 5, 1, 0, true, 900000, 0, [
        {
            grade: 'DANGER', text: '이하늘 회원 페이스 절반', done: true, coachName: '김한솔',
            facts: ['13회 → 6회', '9일 공백', '잔여 4회', 'D-11'],
        },
        {
            grade: 'DANGER', text: '박서준 회원 수업 중단', done: false, coachName: '조은오',
            facts: ['13회 → 3회', '19일 공백', '잔여 2회', 'D-6'],
        },
        {
            grade: 'WARNING', text: '성현 강사 약속 흔들림', done: false,
            facts: ['응답 19시간', '센터 평균 10.3시간'],
        },
        {
            grade: 'CHANCE', text: '최민서 회원 재등록 시점', done: false, coachName: '김지원',
            facts: ['잔여 3회', 'D-18', '3번째 수강권'],
        },
        {
            grade: 'PRAISE', text: '김한솔 강사 일지 100%', done: false,
            facts: ['일지 100%', '센터 평균 48%'],
        },
    ]),
];

export const mockMonthlySummary = {
    month: '2026년 8월',
    defendedRevenue: 6800000,
    lostRevenue: 800000,
    complianceRate: 61,
    predictionAccuracy: 72,
    retainedWithAction: 78.6,
    retainedWithoutAction: 41.2,
    reportedWrong: 1,
};

export const mockMonthlyInsights = {
    critical: {
        title: '이번 달 가장 뼈아팠던 것',
        body:
            '강사 연락 지연으로 VIP 회원 2명이 이탈했습니다. 두 건 모두 브리핑에서 위험으로 표시됐지만 24시간 안에 조치되지 않았습니다.',
        amount: 2400000,
    },
    mvp: {
        title: '이달의 MVP',
        name: '김한솔 강사',
        detail: '지시 이행률 100% · 재등록 방어 5건 성공 · 담당 회원 이탈 0명',
    },
    nextMonth: {
        title: '다음 달 집중 목표',
        body:
            '첫 수강권 회원의 2회차 전환율이 30%로 떨어지고 있습니다. 라포 전체 평균은 31.7%지만, 이 구간은 이탈률 68.3%라 1%p 개선의 가치가 가장 큽니다.',
    },
};
